/**
 * Constants for the Historical Permits Report
 * @module permitReportConstants
 */

/**
 * Escapes a value for CSV format (RFC 4180)
 * @param {*} value - The value to escape
 * @returns {string} The escaped CSV value
 */
const escapeCSVValue = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (
    str.includes(",") ||
    str.includes('"') ||
    str.includes("\n") ||
    str.includes("\r")
  ) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Custom CSV export handler for MaterialTable
 * Properly escapes commas, quotes, and newlines in cell values
 * @param {Array} columns - Column definitions from MaterialTable
 * @param {Array} data - Row data from MaterialTable
 */
export const exportCsvHandler = (columns, data) => {
  const exportableColumns = columns.filter(
    (col) => col.export !== false && col.field
  );
  const headers = exportableColumns.map((col) =>
    escapeCSVValue(col.title || col.field)
  );
  const rows = data.map((row) =>
    exportableColumns.map((col) => escapeCSVValue(row[col.field])).join(",")
  );
  const csvContent = [headers.join(","), ...rows].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `historical_permits_${
    new Date().toISOString().split("T")[0]
  }.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Download format types for permit documents
 * @constant {Object}
 */
export const DOWNLOAD_TYPES = {
  SINGLE_DOCX: "single-docx",
  SINGLE_PDF: "single-pdf",
  BULK_DOCX: "bulk-docx",
  BULK_PDF: "bulk-pdf",
};

/**
 * MaterialTable configuration options
 * @constant {Object}
 */
export const TABLE_OPTIONS = {
  selection: true,
  selectionProps: (rowData) => ({
    disabled: !rowData.id,
  }),
  emptyRowsWhenPaging: false,
  exportAllData: true,
  columnsButton: true,
  exportButton: { csv: true },
  pageSize: 10,
  pageSizeOptions: [5, 10, 30, 60],
  padding: "dense",
  searchFieldAlignment: "left",
  showTitle: false,
  maxBodyHeight: "600px",
};

/**
 * Button style configurations for bulk download actions
 * @constant {Object}
 */
export const BULK_BUTTON_STYLES = {
  docx: {
    backgroundColor: "#1976d2",
    color: "white",
    cursor: "pointer",
  },
  pdf: {
    backgroundColor: "#dc004e",
    color: "white",
    cursor: "pointer",
  },
};

/**
 * Query key for React Query
 * @constant {string}
 */
export const PERMITS_QUERY_KEY = "UiReportAllPermits";

/**
 * React Query options for permits data
 * @constant {Object}
 */
export const QUERY_OPTIONS = {
  keepPreviousData: true,
  refetchOnWindowFocus: false,
};
